import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import webui from "@tinqin/tinqin-web-ui";
import { traverseAndApplyColor } from "../../../common/utils/InlineStyles";
import { xssFilter } from "../../../common/utils/xssFilter";
import { IComponentProps, IExtractedProps } from "../../../common/utils/commonTypes";

interface IArticleTextProps extends IComponentProps {
    getCurrentDashboardId: () => string;
}
type Options = {
    ref: React.MutableRefObject<HTMLElement>;
    "data-hyperlinks-container": boolean;
    id?: string
};
//tagNames to be changed
const NODES_DESCRIPTION = {
    title: {
        tag: "H3",
        newTag: "H2",
        classList: ["tq-title", "tq-bold", "tq-neutral-color-1"],
        newClassList: ["tq-title", "tq-bold", "tq-neutral-color-1", "un-article-content-mid-title"]
    },
    subtitle: {
        tag: "H4",
        newTag: "H3",
        classList: ["tq-title", "un-title-sub"]
    },
    accent: {
        tag: "H5",
        newTag: "DIV",
        classList: ["un-p-accent"]
    }
};
//match tagName and classes
export const checkNode = (node: Element, type: string): boolean => {
    if (node.tagName === NODES_DESCRIPTION[type].tag) {
        let classMatch = true;
        NODES_DESCRIPTION[type].classList.forEach(className => {
            if (classMatch && !node.classList.contains(className)) {
                classMatch = false;
            }
        });
        return classMatch;
    } else {
        return false;
    }
};
//update tagname and copy classes and content
export const updateNode = (node: Element, type: string): void => {
    const newNode: HTMLHeadingElement = document.createElement(NODES_DESCRIPTION[type].newTag);
    const newClassList: string[] =
        NODES_DESCRIPTION[type].newClassList || NODES_DESCRIPTION[type].classList;
    newNode.classList.add(...newClassList);
    newNode.innerHTML = node.innerHTML;
    node.parentNode.replaceChild(newNode, node);
};
//update tagNames and possibly classLists of title, subtitle and accent
export const traverseAndUpdateTagNames = (node: Element): void => {
    if (checkNode(node, "title")) {
        updateNode(node, "title");
        return;
    }
    if (checkNode(node, "subtitle")) {
        updateNode(node, "subtitle");
        return;
    }
    if (checkNode(node, "accent")) {
        updateNode(node, "accent");
        return;
    }
    for (let i = 0; i < node.children.length; ++i) {
        traverseAndUpdateTagNames(node.children[i]);
    }
};
//for offer articles only update classList of title elements
export const traverseAndUpdateTitle = (node: Element): void => {
    if (checkNode(node, "title")) {
        node.classList.add("un-article-content-mid-title");
        return;
    }
    for (let i = 0; i < node.children.length; ++i) {
        traverseAndUpdateTitle(node.children[i]);
    }
};
//Same as RichText, applies colors to inner elements that are preceded by parents
export const ArticleText: React.FC<IArticleTextProps> = props => {
    const dashboardId: string = props.getCurrentDashboardId();
    const [offer] = useState<boolean>(dashboardId === "article-offer");
    const mainNode = useRef<HTMLElement>();

    const changeDomNodes = useCallback((): void => {
        if (mainNode.current) {
            traverseAndApplyColor(mainNode.current);
            if (offer) {
                traverseAndUpdateTitle(mainNode.current);
            } else {
                traverseAndUpdateTagNames(mainNode.current);
            }
        }
    }, [offer]);

    useEffect(() => {
        changeDomNodes();
    });

    const data: IArticleTextProps["data"] = props.data ? props.data.properties || {} : null;
    const dataProps: IExtractedProps = webui.getUtils().extractComponentProps(props, {
        additionalProps: ["tag", "value", "extraClasses", "anchorId"],
        propsDefaults: {
            tag: "div",
            extraClasses: ""
        }
    });
    const innerHTML: string = useMemo(() => xssFilter.process(dataProps.value), [dataProps.value]);
    if (!data) {
        return null;
    }
    const options: Options = {
        ref: mainNode,
        "data-hyperlinks-container": true
    };
    if (dataProps.anchorId) {
        options.id = dataProps.anchorId;
    }

    return React.createElement(dataProps.tag, {
        className: dataProps.extraClasses,
        dangerouslySetInnerHTML: {
            __html: innerHTML
        },
        ...options
    });
};

export default webui.connectComponent({
    actions: {
        getCurrentDashboardId: "uneo.getCurrentDashboardId"
    }
})(ArticleText);
